import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'

//plugins
import { Modal, Button, Row, Col, FloatingLabel, Form } from 'react-bootstrap';

//helpers
import Api from 'helpers/Api'
import { useValidation } from 'helpers/Validation'
import { useNestedState } from 'helpers/NestedState'

//providers
import { useCurrencyContext } from 'providers/Currency';

//misc
import Autocomplete from 'components/misc/Autocomplete';
import SaveButton from 'components/misc/Button'
import Info from 'components/modals/Info';

function AddOrEdit(props, ref) {

    const currencies = useCurrencyContext();

    const defaultData = {
        langs: {}
    }

    const [state, setState] = useNestedState({
        show: false,
        id: null,
        edit: false,
        data: defaultData,
        initialData: defaultData,
        loading: false,
        onSuccess: null,
        onEntering: null,
        onExiting: null
    });

    const [validations, setValidations] = useValidation();
    const validationModalRef = useRef(null);

    useImperativeHandle(ref, () => ({
        add: () => {
            handleShow();
        },
        edit: (id) => {
            edit(id);
        },
        hide: () => {
            hide();
        },
        onSuccess: fn => {
            setState(prev => ({
                ...prev,
                onSuccess: fn
            }));
        }
    }));

    useEffect(() => {
        loadData();
    }, [state.edit]);

    const show = (onEntering = null) => {
        setState(prev => ({
            ...prev,
            show: true,
            onEntering: onEntering,
        }));
    }

    const hide = (onExiting = null) => {
        setState(prev => ({
            ...prev,
            show: false,
            onExiting: onExiting
        }));
    }

    const handleShow = () => {
        setState(prev => ({
            ...prev,
            data: defaultData,
            id: null,
        }));

        show();
    }

    const handleClose = e => {

        if (e) {
            e.preventDefault();
        }

        setState(prev => ({
            ...prev,
            data: defaultData,
            id: null,
        }));

        setValidations(null);

        hide();
    }

    const edit = id => {
        setState(prev => ({
            ...prev,
            id: id,
            edit: new Date().getTime()
        }));

        show();
    }

    const loadData = () => {
        if (!state.id) {
            return;
        }

        Api.get('currencies/edit?id=' + state.id)
            .then(res => {
                setState(prev => ({
                    ...prev,
                    data: res.data,
                    initialData: res.data
                }))
            });
    }


    const handleSave = e => {
        e.preventDefault();

        setState(prev => ({
            ...prev,
            loading: true,
        }));

        setValidations(null);

        let url = state.id ? 'currencies/update' : 'currencies/add';

        let data = new FormData(e.target);

        if (state.id) {
            data.append('id', state.id)
        }

        Api.post(url, data, {
            headers: {
                'content-type': 'multipart/form-data'
            }
        }).then(res => {
            if (typeof state.onSuccess === 'function') {
                state.onSuccess(res.data)
            }

            hide()
        }).catch(error => {
            const _err = error.response;
            if (_err && _err.status && _err.status === 422) {
                hide(() => {
                    let modal = validationModalRef.current;

                    if (modal) {
                        modal.open();

                        modal.onClose(() => {
                            setValidations(_err.data.errors)
                            show();
                        });
                    }
                });
            }
        }).finally(() => {
            setState(prev => ({
                ...prev,
                loading: false,
            }));
        });
    }

    const handleCheckboxChange = e => {
        const name = e.target.name;
        setState(prev => ({
            ...prev,
            data: {
                ...prev.data,
                [name]: prev.data[name] ? false : true
            }
        }));
    }


    const handleInputChange = e => {
        const name = e.target.name
        const value = e.target.value

        setState(name, value, 'data')
    }

    const handleDateChange = e => {
        const name = e.target.name + '_ymd'
        const value = e.target.value

        setState(name, value, 'data')
    }

    const handleSync = e => {
        e.preventDefault();
        if (state.data.name) {
            let url = `currencies/sync?name=${state.data.name}`;

            Api.post(url).then(res => {
                setState(prev => ({
                    ...prev,
                    data: {
                        ...prev.data,
                        'rate': res.data
                    }
                }));
            });
        }

    }

    const handleCurrencyChange = (data) => {
        setState(prev => ({
            ...prev,
            data: {
                ...prev.data,
                'name': data.name
            }
        }));
    }

    return (
        <>
            <Info
                ref={validationModalRef}
                mainMessage="Моля, попълнете всички задължителни полета, означени с червен цвят!"
            />


            <Modal centered show={state.show} onHide={handleClose} onEntering={state.onEntering} onExiting={state.onExiting}>
                <form onSubmit={handleSave}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            Валута
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>

                        <Col className="mb-3">
                            <Autocomplete
                                url="autocomplete/currencies"
                                inputPlaceholder="Име"
                                inputName={`name`}
                                inputValue={state.data.name || ''}
                                inputIdValue={state.data.name || ''}
                                error={Boolean(validations?.name)}
                                helperText={validations?.name}
                                onChange={data => handleCurrencyChange(data)}
                            />

                        </Col>

                        <Row className="mb-3">
                            <Col>
                                <FloatingLabel label="Знак - префикс">
                                    <Form.Control
                                        type="text"
                                        name={`prefix`}
                                        value={state.data.prefix || ''}
                                        placeholder="Знак - префикс"
                                        onChange={(e) => handleInputChange(e)}
                                        isInvalid={Boolean(validations?.prefix)}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {validations?.prefix}
                                    </Form.Control.Feedback>
                                </FloatingLabel>
                            </Col>
                            <Col>
                                <FloatingLabel label="Знак - суфикс">
                                    <Form.Control
                                        type="text"
                                        name={`sufix`}
                                        value={state.data.sufix || ''}
                                        placeholder="Знак - суфикс"
                                        onChange={(e) => handleInputChange(e)}
                                        isInvalid={Boolean(validations?.sufix)}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {validations?.sufix}
                                    </Form.Control.Feedback>
                                </FloatingLabel>
                            </Col>
                        </Row>

                        <Row className="mb-3" style={{ alignItems: 'center' }}>
                            <Col xs="6">
                                <FloatingLabel label="Курс">
                                    <Form.Control
                                        type="number"
                                        name={`rate`}
                                        value={Number(state.data?.default) === 1 ? 1 : state.data.rate || ''}
                                        placeholder="Курс"
                                        disabled={Number(state.data?.default) === 1 ? 'disabled' : ''}
                                        onChange={(e) => handleInputChange(e)}
                                        isInvalid={Boolean(validations?.rate)}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {validations?.rate}
                                    </Form.Control.Feedback>
                                </FloatingLabel>
                            </Col>

                            {Number(state.data?.default) !== 1 ? <Col>
                                <Button onClick={(e) => handleSync(e)} variant="outline-dark" size="sm">
                                    Вземи курса
                                </Button>
                            </Col> : ''}
                        </Row>

                        {Number(state.initialData?.default) !== 1 ? <Form.Check
                            type="switch"
                            name="default"
                            label="По подразбиране"
                            className="mb-3 mt-3"
                            checked={Number(state.data?.default) === 1}
                            onChange={handleCheckboxChange}
                        /> : ''}

                        <Form.Check
                            type="switch"
                            name="sync"
                            label="Автоматично синхронизиране"
                            className="mb-3 mt-3"
                            checked={Number(state.data?.sync) === 1}
                            onChange={handleCheckboxChange}
                        />

                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleClose}>
                            Отказ
                        </Button>
                        <SaveButton
                            loading={state.loading}
                            className="save"
                        />
                    </Modal.Footer>
                </form>
            </Modal>
        </>
    )
}

export default forwardRef(AddOrEdit);
